/**
 * Master Data Edit API Module
 */

import { ApiBase, HttpMethod } from "../core/api-base";
import { notifier } from "../core/notifier/notifier";
import type * as z from "zod";
import {
    CreateItem201Response as CreateBrandItem201Response,
    DeleteItem200Response as DeleteBrandItem200Response,
    UpdateItem200Response as UpdateBrandItem200Response,
} from "../api/generated/brands.zod";
import {
    CreateItem201Response as CreateProductItem201Response,
    DeleteItem200Response as DeleteProductItem200Response,
    UpdateItem200Response as UpdateProductItem200Response,
    UploadImage200Response,
} from "../api/generated/products.zod";
import {
    CreateItem201Response as CreateUserItem201Response,
    DeleteItem200Response as DeleteUserItem200Response,
    UpdateItem200Response as UpdateUserItem200Response,
} from "../api/generated/users.zod";
import { CommonUtils } from "../util/common-utils";

type MasterDataEditApiScalar = string | number | boolean | null | undefined | File;

type MasterDataEditApiValue = MasterDataEditApiScalar | MasterDataEditApiRecord | MasterDataEditApiValue[];

type MasterDataEditApiRecord = { [key: string]: MasterDataEditApiValue };

type RequestPayload = MasterDataEditApiRecord | FormData | string | null;

type MasterDataEditApiData = {
    item?: MasterDataEditApiRecord | null;
    filename?: string | null;
    image_url?: string | null;
    message?: string | null;
    [key: string]: MasterDataEditApiValue;
};

type MasterDataEditMutationResponse =
    | z.infer<typeof CreateBrandItem201Response>
    | z.infer<typeof CreateProductItem201Response>
    | z.infer<typeof CreateUserItem201Response>
    | z.infer<typeof UpdateBrandItem200Response>
    | z.infer<typeof UpdateProductItem200Response>
    | z.infer<typeof UpdateUserItem200Response>;

type MasterDataEditDeleteResponse =
    | z.infer<typeof DeleteBrandItem200Response>
    | z.infer<typeof DeleteProductItem200Response>
    | z.infer<typeof DeleteUserItem200Response>;

type MasterDataEditUploadResponse = z.infer<typeof UploadImage200Response>;

type MasterDataEditApiRawResponse =
    | MasterDataEditMutationResponse
    | MasterDataEditDeleteResponse
    | MasterDataEditUploadResponse;

export type MasterDataEditApiResponse = {
    success?: boolean | number;
    message?: string;
    data?: MasterDataEditApiData | null;
    [key: string]: MasterDataEditApiValue;
};

function isApiRecord(value: unknown): value is MasterDataEditApiRecord {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasItemRecord(
    value: MasterDataEditApiRawResponse,
): value is MasterDataEditApiRawResponse & { item?: MasterDataEditApiRecord | null } {
    return isApiRecord(value) && "item" in value && isApiRecord((value as MasterDataEditApiRecord).item);
}

function hasDataRecord(
    value: MasterDataEditApiRawResponse,
): value is MasterDataEditApiRawResponse & { data?: MasterDataEditApiRecord | null } {
    return isApiRecord(value) && "data" in value && isApiRecord((value as MasterDataEditApiRecord).data);
}

function normalizeMasterDataEditResponse(raw: MasterDataEditApiRawResponse): MasterDataEditApiResponse {
    const normalized = { ...(raw as MasterDataEditApiRecord) } as MasterDataEditApiResponse;

    if (hasItemRecord(raw) && !hasDataRecord(raw)) {
        normalized.data = {
            item: raw.item,
        };
    } else if (hasDataRecord(raw)) {
        normalized.data = raw.data as MasterDataEditApiData;
    }

    if (!normalized.message && typeof normalized.data?.message === "string") {
        normalized.message = normalized.data.message;
    }

    return normalized;
}

class MasterDataEditApi extends ApiBase {
    public static requestCreateItem(url: string, payload: RequestPayload): Promise<Response> {
        return super.apiRequest(url, HttpMethod.Post, payload);
    }

    public static requestUpdateItem(url: string, payload: RequestPayload): Promise<Response> {
        return super.apiRequest(url, HttpMethod.Put, payload);
    }

    public static requestDeleteItem(url: string): Promise<Response> {
        return super.apiRequest(url, HttpMethod.Delete, null);
    }

    public static requestUploadImage(url: string, formData: FormData): Promise<Response> {
        return super.apiRequest(url, HttpMethod.Post, formData);
    }
}

async function requestCreateItem(url: string, payload: RequestPayload): Promise<Response> {
    return MasterDataEditApi.requestCreateItem(url, payload);
}

async function requestUpdateItem(url: string, payload: RequestPayload): Promise<Response> {
    return MasterDataEditApi.requestUpdateItem(url, payload);
}

async function requestDeleteItem(url: string): Promise<Response> {
    return MasterDataEditApi.requestDeleteItem(url);
}

async function requestUploadImage(url: string, formData: FormData): Promise<Response> {
    return MasterDataEditApi.requestUploadImage(url, formData);
}

function resolveMasterUrl(url: string): string {
    return CommonUtils.resolveUrl(String(url));
}

async function handleResponse(response: Response, fallbackMessage: string): Promise<MasterDataEditApiResponse | null> {
    if (!response.ok) {
        await notifier.fromResponse(response, fallbackMessage);
        return null;
    }

    const rawJson = (await response.json().catch(() => null)) as MasterDataEditApiRawResponse | null;
    if (!rawJson || !isApiRecord(rawJson)) {
        notifier.error(fallbackMessage);
        return null;
    }

    const json = normalizeMasterDataEditResponse(rawJson);
    const success = json?.success;
    if (success !== undefined && !(success === 1 || success === true)) {
        notifier.error(json.message || fallbackMessage);
        return null;
    }

    return json;
}

export {
    requestCreateItem,
    requestUpdateItem,
    requestDeleteItem,
    requestUploadImage,
    resolveMasterUrl,
    handleResponse,
};
